import {
  Dialog,
  DialogActions,
  DialogContent,
  MultiSelectChipInput,
  SubmitButton,
  useSnackbarContext,
} from "@ameelio/ui";
import { useMutation, useQuery } from "@apollo/client/react";
import { Alert, Stack } from "@mui/material";
import { useForm } from "react-hook-form";
import { TabletMdm } from "../api/graphql";
import { ContentSkeleton } from "../lib/closet";
import useApolloErrorHandler from "../lib/handleApolloError";
import { GetAvailableTabletGroupsDocument } from "./GetAvailableTabletGroups.generated";
import { SetFacilityTabletGroupsDocument } from "./SetFacilityTabletGroups.generated";

// Esper is the only supported MDM for now. Add an MDM selector here when a
// second one is introduced.
const MDM = TabletMdm.Esper;

type Props = {
  facilityId: string;
  onClose: () => void;
};

type FormData = {
  externalIds: string[];
};

export default function UpdateFacilityTabletGroupsDialog({
  facilityId,
  onClose,
}: Props) {
  const { data, error } = useQuery(GetAvailableTabletGroupsDocument, {
    variables: { facilityId, mdm: MDM },
    fetchPolicy: "network-only",
  });

  if (error) throw error;

  return (
    <Dialog title="Tablet groups" onClose={onClose}>
      {data ? (
        <TabletGroupsForm
          facilityId={facilityId}
          groups={data.availableTabletGroups}
          onClose={onClose}
        />
      ) : (
        <DialogContent sx={{ width: 500 }}>
          <ContentSkeleton />
        </DialogContent>
      )}
    </Dialog>
  );
}

function TabletGroupsForm({
  facilityId,
  groups,
  onClose,
}: Props & {
  groups: { externalId: string; name: string; selected: boolean }[];
}) {
  const handleApolloError = useApolloErrorHandler();
  const snackbarContext = useSnackbarContext();

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<FormData>({
    defaultValues: {
      externalIds: groups.filter((g) => g.selected).map((g) => g.externalId),
    },
  });

  const [setGroups] = useMutation(SetFacilityTabletGroupsDocument, {
    onError: (e) => handleApolloError(e),
  });

  const onSubmit = async ({ externalIds }: FormData) => {
    await setGroups({
      variables: {
        input: {
          facilityId,
          mdm: MDM,
          groups: groups
            .filter((g) => externalIds.includes(g.externalId))
            .map(({ externalId }) => ({ externalId })),
        },
      },
      onCompleted: () => {
        snackbarContext.alert("success", "Tablet groups updated");
        onClose();
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DialogContent sx={{ width: 500 }}>
        <Stack spacing={2}>
          {groups.length === 0 ? (
            <Alert severity="info">No groups found in Esper.</Alert>
          ) : (
            <MultiSelectChipInput
              control={control}
              name="externalIds"
              label="Esper groups"
              keepMenuOpen
              items={groups.map((g) => ({
                value: g.externalId,
                name: g.name,
              }))}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <SubmitButton submitting={isSubmitting}>Submit</SubmitButton>
      </DialogActions>
    </form>
  );
}
